import { RelatedBlogPost } from "@/types/contentful/blog";
import {
  Breadcrumb,
  TourHeader,
  MediaCarouselItem,
  AccommodationItem
} from "@/types/dto/pdp/pdp-header-about";
import { TourCarousel } from "@/types/dto/destinations-page/destination-page-types";
import { Tour } from "@/types/dto/tours/tour.d";
import type { SimilarTour } from "@/data-fetch/pdp/get-similar-tours";

export const breadcrumbFactory = (data: any = {}): Breadcrumb => ({
  continent: data?.continent ?? "",
  country: data?.country ?? "",
  tourTitle: data?.tourTitle ?? ""
});

// Enhanced factory that accepts both data sources
export const tourHeaderFactory = (solrData: any = {}, contentfulData: any = {}): TourHeader => ({
  // Solr-sourced fields
  tourTitle: solrData?.tourTitle ?? "",
  tourSubtitle: solrData?.tourSubtitle ?? undefined,
  days: solrData?.days ?? 0,
  groupSizeAvg: solrData?.groupSizeAvg ?? 0,
  groupSizeMax: solrData?.groupSizeMax ?? 0,
  price: solrData?.price ?? 0,
  crossedOutPrice: solrData?.crossedOutPrice ?? undefined,
  offer: solrData?.offer ?? undefined,
  newTour: solrData?.newTour ?? false,
  onTourAir: solrData?.onTourAir ?? false,
  activityLevelNumber: solrData?.activityLevelNumber ?? 1,
  activityLevelTitle: solrData?.activityLevelTitle ?? "",
  tags: solrData?.tags ?? [],
  travelStyles: solrData?.travelStyles ?? [],
  stars: solrData?.stars >= 4 ? solrData.stars : undefined,
  reviewCount: solrData?.reviewCount ?? undefined,
  totalMeals: solrData?.totalMeals ?? "",
  // currencyId: solrData?.currencyId ?? "",
  //  marketId: solrData?.marketId ?? "",
  // Contentful-sourced fields
  tourId: contentfulData?.tourContent?.neoId ?? "",
  landingPageId: contentfulData?.tourContent?.landingPageId ?? "",
  onTourAirModalContent: contentfulData?.modalContent?.onTourAir,
  travelStylesModal: contentfulData?.modalContent?.travelStyles,
  activityLevelsModal: contentfulData?.modalContent?.activityLevels
});

export const mediaCarouselItemFactory = (data: any = {}): MediaCarouselItem => ({
  id: data?.id ?? 0,
  url: data?.url ?? "",
  altTag: data?.altTag ?? "",
  videoId: data?.videoId
});

export const tourMediaGalleryFactory = (data: any = {}): MediaCarouselItem[] => {
  const mediaItems = data ?? [];
  return mediaItems.map((item: any) => mediaCarouselItemFactory(item));
};

export const accommodationsFactory = (data: any[] = []): AccommodationItem[] => {
  if (!Array.isArray(data)) return [];
  return data.map((item: any) => ({
    id: item?.id ?? 0,
    night: item?.night ?? "",
    price: item?.price ?? "",
    hotelName: item?.hotelName ?? "",
    city: item?.city ?? "",
    country: item?.country ?? "",
    imageUrl: item?.imageUrl ?? "",
    imageAltTag: item?.imageAltTag ?? "Hotel Image",
    hotelLink: item?.hotelLink ?? ""
  }));
};

const normalizeImageUrl = (imageUrl: string | undefined): string => {
  if (!imageUrl) return "";
  if (imageUrl.startsWith("https:")) return imageUrl;
  return `https:${imageUrl}`;
};

export const transformRelatedBlogUrl = (url: string): string => {
  if (!url) return url;

  const [pathname, query] = url.split("?");
  const pathSegments = pathname.split("/");

  if (pathSegments.includes("traveling-well") || pathSegments.includes("travel-blog")) {
    const blogSlug = pathSegments.filter(Boolean).at(-1);

    if (blogSlug) {
      return `/travel-blog/${blogSlug}${query ? `?${query}` : ""}`;
    }
  }

  return url;
};

export const pdpRelatedBlogsFactory = (data: any = {}): RelatedBlogPost[] => {
  if (!Array.isArray(data)) return [];
  return data.map((item: any, index) => ({
    id: item?.id ?? index,
    title: item?.title ?? "",
    summary: item?.summary ?? "",
    slug: item?.slug ?? "",
    url: transformRelatedBlogUrl(item?.url ?? ""),
    heroImage: {
      url: normalizeImageUrl(item?.imageUrl),
      altText: item?.altText ?? `Blog Image-${index}`
    },
    publishedDate: item?.publishedDate
      ? new Date(item.publishedDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
      : "",
    minutesToRead: item?.minutesToRead ?? 0
  }));
};

const offerSavingsFactory = (maxOffer: string): string | undefined => {
  const normalizedOffer = maxOffer.trim();

  return normalizedOffer && normalizedOffer !== "0" ? maxOffer : undefined;
};

export const pdpSimilarToursFactory = (data: any = {}): TourCarousel => {
  const similarTours: SimilarTour[] = data?.tours ?? [];
  const basicInfo = data?.basicInfo ?? {};
  return {
    basicInfo: {
      headerText: basicInfo?.headerText,
      copyText: basicInfo?.copyText,
      callToActionText: basicInfo?.callToActionText ?? "",
      callToActionUrl: basicInfo?.callToActionUrl ?? ""
    },
    tours: similarTours.map(
      (tour): Tour => ({
        neoId: tour.landingPageId,
        id: tour.landingPageId,
        title: [tour.title, tour.subTitle].filter(Boolean).join(" "),
        shortTitle: tour.title,
        searchImage: {
          imageUrl: tour.image,
          altText: tour.title
        },
        heroImage: {
          imageUrl: tour.image,
          altText: tour.title
        },
        days: parseInt(tour.length, 10) || 0,
        price: tour.minimumPrice.toString(),
        strikeOutPrice: tour.strikeOutPrice ?? undefined,
        offerSavings: offerSavingsFactory(tour.maxOffer),
        tourUrl: tour.url,
        badges: [
          tour.isNewTour === true ? "New Tour" : undefined,
          Array.isArray(tour.productLines) && tour.productLines.includes("Explorations")
            ? "Small Group"
            : undefined
        ].filter((badge): badge is string => Boolean(badge)),
        description: "",
        mediumDescription: "",
        cities: [],
        countries: [],
        continents: [],
        pageViews: 0
      })
    )
  };
};
